import {VehicleData} from '@mira/api';
import {Box, VStack} from 'platform/foundation-tw';
import {z} from 'zod';

import {useState} from 'react';
import {useTranslation} from 'react-i18next';

import {isNil} from 'ramda';

import {Banner} from '../../components/Banner/Banner';
import {PredictionReport} from '../../components/PredictionReport/PredictionReport';
import {usePredictionFlow} from '../../hooks/usePredictionFlow';
import {useVehicleQueryParams, VehicleQueryParams} from '../../hooks/useVehicleQueryParams';
import {mapBodyStyleToCarStyle} from '../../utils/mapBodyStyleToCarStyle';

const REQUIRED_FIELDS: (keyof VehicleQueryParams)[] = [
  'make',
  'modelFamily',
  'bodyStyle',
  'engineVolume',
  'transmission',
  'fuelType',
  'modelYear',
  'mileage',
];

function getMissingFields(params: VehicleQueryParams): string[] {
  return REQUIRED_FIELDS.filter((field) => isNil(params[field]));
}

function formatInvalidFields(errors: z.ZodError): string {
  return errors.issues.map((issue) => `${issue.path.join('.')} (${issue.message})`).join(', ');
}

function mapToVehicleData(params: VehicleQueryParams): VehicleData | undefined {
  if (
    isNil(params.make) ||
    isNil(params.modelFamily) ||
    isNil(params.bodyStyle) ||
    isNil(params.engineVolume) ||
    isNil(params.transmission) ||
    isNil(params.fuelType) ||
    isNil(params.modelYear) ||
    isNil(params.mileage)
  ) {
    return undefined;
  }

  return {
    make: params.make,
    model_family: params.modelFamily,
    car_style: mapBodyStyleToCarStyle(params.bodyStyle),
    body_style: params.bodyStyle, // legacy, kept for v0/v1 fallback
    engine_volume: params.engineVolume,
    transmission: params.transmission,
    fuel_type: params.fuelType,
    model_year: params.modelYear,
    first_registration: params.firstRegistration,
    mileage: params.mileage,
  };
}

export function OmneticReport() {
  const {t} = useTranslation();
  const {data: queryParams, errors: queryErrors} = useVehicleQueryParams();

  if (!isNil(queryErrors)) {
    return (
      <ReportError
        title={t('omneticReport.error.invalidValues.title')}
        message={t('omneticReport.error.invalidValues', {fields: formatInvalidFields(queryErrors)})}
      />
    );
  }

  if (isNil(queryParams)) {
    return (
      <ReportError
        title={t('omneticReport.error.noParameters.title')}
        message={t('omneticReport.error.noParameters')}
      />
    );
  }

  const vehicleData = mapToVehicleData(queryParams);

  if (isNil(vehicleData)) {
    const missingFields = getMissingFields(queryParams);

    return (
      <ReportError
        title={t('omneticReport.error.missingFields.title')}
        message={t('omneticReport.error.missingFields', {fields: missingFields.join(', ')})}
      />
    );
  }

  return (
    <OmneticReportContent
      vin={queryParams.vin}
      vehicleData={vehicleData}
      make={queryParams.make}
      makeLabel={queryParams.makeLabel}
      modelTitle={queryParams.modelTitle}
    />
  );
}

interface ReportErrorProps {
  title: string;
  message: string;
}

function ReportError(props: ReportErrorProps) {
  return (
    <Box maxWidth="108rem" center minHeight="100dvh">
      <Box paddingHorizontal={8} paddingTop={8}>
        <VStack spacing={4}>
          <Banner
            tone="error"
            title={props.title}
            description={props.message}
            testId="invalidReportParametersBanner"
          />
        </VStack>
      </Box>
    </Box>
  );
}

interface OmneticReportContentProps {
  vin: string | undefined;
  vehicleData: VehicleData;
  make: string | undefined;
  makeLabel: string | undefined;
  modelTitle: string | undefined;
}

function OmneticReportContent(props: OmneticReportContentProps) {
  const {predictionData, rulData, isLoading, error} = usePredictionFlow({
    vin: props.vin,
    vehicleData: props.vehicleData,
  });
  const [selectedPartId, setSelectedPartId] = useState<string | null>(null);

  const handlePartClick = (partId: string | null) => {
    setSelectedPartId(partId);
  };

  return (
    <PredictionReport
      vin={props.vin ?? ''}
      predictionData={predictionData}
      rulData={rulData}
      isLoading={isLoading}
      error={error}
      selectedPartId={selectedPartId}
      onPartClick={handlePartClick}
      make={props.make}
      makeLabel={props.makeLabel}
      modelTitle={props.modelTitle}
    />
  );
}
